/**
 * Derives a best-guess display name from an email address.
 *
 * Only the local-part (before the first `@`) is inspected. `+tag` suffixes
 * are stripped, and the common separators `.`, `_`, `-` are treated as
 * word boundaries. Each word is title-cased.
 *
 * Returns `null` when the local-part is empty or contains nothing but
 * separators/digits — the caller is expected to fall back to the raw email.
 *
 * Intended use is **pre-signup presentation only** (e.g. the pending
 * invitations list, the invite email subject). Once the invitee accepts and
 * their profile row exists, `profiles.display_name` is canonical and this
 * helper should not be used.
 *
 * Examples:
 *   tom@rtp.vc            → "Tom"
 *   john.smith@acme.com   → "John Smith"
 *   jane_doe@acme.com     → "Jane Doe"
 *   j-smith@acme.com      → "J Smith"
 *   tom+projects@rtp.vc   → "Tom"
 *   admin@acme.com        → "Admin"
 *   123@acme.com          → null  (no letter words)
 *   @acme.com             → null  (empty local-part)
 */
export function nameFromEmail(email: string | null | undefined): string | null {
  if (!email) return null
  const at = email.indexOf('@')
  // Treat "nolocal@..." and a missing `@` as unusable. A bare local-part
  // without `@` is arguably valid ("admin"), but in our invite context the
  // caller only passes RFC-shaped emails — return null for anything weird.
  const local = at > 0 ? email.slice(0, at) : ''
  if (!local) return null

  // Strip `+tag` sub-addressing: "tom+projects" → "tom"
  const stripped = local.split('+')[0]

  // Replace common separators with spaces, collapse whitespace, trim.
  const words = stripped
    .replace(/[._-]+/g, ' ')
    .split(/\s+/)
    .map((w) => w.trim())
    .filter(Boolean)
    // Drop all-digit words — `john.123` → "John", `123` → null.
    .filter((w) => /[a-zA-Z]/.test(w))

  if (words.length === 0) return null

  return words
    .map((w) => w[0]!.toUpperCase() + w.slice(1).toLowerCase())
    .join(' ')
}
